import { GROUP_SEPARATOR_GAP, VALUE_ITEMS_GAP } from "./constants";

export const calculateAvailableWidth = (fullWidth: number, titleWidth: number): number => {
  return fullWidth - titleWidth;
};

export const calculateWidthPerGroup = (availableWidth: number, numGroups: number): number => {
  const totalSeparatorsWidth = Math.max(0, numGroups - 1) * GROUP_SEPARATOR_GAP;
  return (availableWidth - totalSeparatorsWidth) / numGroups;
};

/**
 * Calculates how many values can be displayed in each group using equal width allocation.
 *
 * This function implements a fair distribution strategy where each group (e.g., EVO, LYO)
 * receives an equal portion of the available width, rather than a sequential left-to-right
 * allocation. Each group gets `availableWidth / numGroups`.
 *
 * For each group:
 * 1. If the group has a label (e.g., "EVO:", "LYO:"), measures its width first
 * 2. If the label alone exceeds the group's allocated width, that group shows 0 values
 * 3. Accumulates widths of values (measured from DOM refs) until the group's width is filled
 * 4. Each item includes VALUE_ITEMS_GAP spacing in its width calculation
 *
 * @param groupedByLabel - Values organized by their label (e.g., { "EVO": ["Mint", "Berry"], "LYO": ["Citrus"] })
 * @param itemRefs - React refs array containing rendered DOM elements for width measurement
 * @param widthPerGroup - The equal width allocation each group receives (availableWidth / numGroups)
 * @returns Object mapping each label to the count of values that fit within its allocated width
 *
 * @example
 * // With 300px available and 2 groups (EVO, LYO), each gets 150px
 * calculateVisibleGroupCount(
 *   { "EVO": ["Mint", "Berry", "Citrus"], "LYO": ["Orange", "Lemon"] },
 *   itemRefs,
 *   150
 * )
 * // Returns: { "EVO": 2, "LYO": 2 } if only 2 values fit per group
 */
export const calculateVisibleGroupCount = (
  groupedByLabel: Record<string, string[]>,
  itemRefs: React.MutableRefObject<(HTMLSpanElement | null)[]>,
  widthPerGroup: number
): Record<string, number> => {
  const visibleCounts: Record<string, number> = {};
  let currentIndex = 0;

  Object.keys(groupedByLabel).forEach((label) => {
    const groupValues = groupedByLabel[label];
    let groupUsedWidth = 0;
    let groupCount = 0;

    // Measure the label width if present (e.g., "EVO: " or "LYO: ")
    if (label) {
      const labelElement = itemRefs.current[currentIndex];
      if (labelElement) {
        const labelWidth = labelElement.offsetWidth + VALUE_ITEMS_GAP;
        // If label alone exceeds allocated width, skip this entire group
        if (labelWidth > widthPerGroup) {
          visibleCounts[label] = 0;
          currentIndex += 1 + groupValues.length;
          return;
        }
        groupUsedWidth += labelWidth;
        currentIndex++;
      }
    }

    // Accumulate values until group's allocated width is filled
    for (let i = 0; i < groupValues.length; i++) {
      const el = itemRefs.current[currentIndex];
      if (!el) break;

      const itemWidth = el.offsetWidth + VALUE_ITEMS_GAP;
      if (groupUsedWidth + itemWidth > widthPerGroup) break;

      groupUsedWidth += itemWidth;
      groupCount++;
      currentIndex++;
    }

    visibleCounts[label] = groupCount;
    // Skip over hidden values in the ref array
    currentIndex += groupValues.length - groupCount;
  });

  return visibleCounts;
};

export const calculateVisibleCount = <T>(
  values: Array<{ value: T }>,
  itemRefs: React.MutableRefObject<(HTMLSpanElement | null)[]>,
  availableWidth: number
): number => {
  const { count } = values.reduce(
    (acc, _, index) => {
      const el = itemRefs.current[index];
      if (!el) return acc;

      const nextTotal = acc.total + el.offsetWidth + VALUE_ITEMS_GAP;
      if (nextTotal > availableWidth) return acc;

      return {
        total: nextTotal,
        count: acc.count + 1,
      };
    },
    { total: 0, count: 0 }
  );

  return count;
};
